import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  HiOutlineCalendar,
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineChartSquareBar,
  HiOutlineBell,
  HiOutlineStar,
  HiOutlineDeviceMobile,
  HiOutlineSparkles
} from 'react-icons/hi'
import { FiArrowUpRight, FiCheck } from 'react-icons/fi'

/* ============================================================
 *  Services / Beautefeel 產品專頁
 * ============================================================ */

const Page = styled.div`
  background: var(--color-bg);
  color: var(--color-ink);
  min-height: 100vh;
`

/* -------- Hero -------- */
const Hero = styled.section`
  position: relative;
  padding: 9rem 1.5rem 4rem;

  @media (min-width: 1440px) {
    padding: 10rem 2rem 5rem;
  }
  @media (max-width: 768px) {
    padding: 6.5rem 1.25rem 1.5rem;
  }
`

const HeroBg = styled.div`
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: 4rem 3rem;
  border-radius: var(--radius-xl);
  background: linear-gradient(150deg, #fbeef3 0%, #f7e1e8 45%, #fcf8f9 100%);
  border: 1px solid var(--color-line);
  overflow: hidden;
  box-shadow: var(--shadow-card);

  @media (max-width: 900px) {
    padding: 2.5rem 1.75rem;
    border-radius: var(--radius-lg);
  }
  @media (max-width: 640px) {
    padding: 1.75rem 1.25rem;
  }
`

const HeroInner = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 3.5rem;
  align-items: center;

  @media (min-width: 1440px) {
    gap: 5rem;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
  @media (max-width: 640px) {
    gap: 1.75rem;
  }
`

const HeroText = styled.div`
  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #d5728a;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h1 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.9rem, 5vw, 4rem);
    line-height: 1.1;
    font-weight: 700;
    color: #1b0d12;
    margin-bottom: 1.25rem;
    letter-spacing: -0.01em;
  }
  h1 em {
    font-style: italic;
    color: #d5728a;
    font-weight: 500;
  }
  p {
    font-size: 1.08rem;
    line-height: 1.8;
    color: #4b2b37;
    max-width: 540px;
    margin-bottom: 2rem;
  }
  @media (max-width: 640px) {
    h1 {
      margin-bottom: 0.9rem;
    }
    p {
      font-size: 0.95rem;
      line-height: 1.7;
      margin-bottom: 1.4rem;
    }
  }
`

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: 0.6rem;
    button {
      padding: 0.8rem 1.3rem;
      font-size: 0.88rem;
    }
  }
`

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--color-ink);
  color: #fff;
  border: none;
  padding: 1rem 1.75rem;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: var(--radius-pill);
  transition: all 0.25s ease;

  &:hover {
    background: var(--color-primary);
    transform: translateY(-2px);
  }
`

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--color-ink);
  border: 1px solid var(--color-ink);
  padding: 1rem 1.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: var(--radius-pill);
  transition: all 0.25s ease;

  &:hover {
    background: var(--color-ink);
    color: #fff;
  }
`

const HeroPanel = styled(motion.div)`
  background: #fff;
  border: 1px solid var(--color-line);
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-heavy);

  @media (max-width: 640px) {
    padding: 1.25rem 1.1rem;
    border-radius: var(--radius-md);
  }
`

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--color-line);
  margin-bottom: 1.25rem;

  @media (max-width: 640px) {
    padding-bottom: 0.75rem;
    margin-bottom: 0.4rem;
  }
  .title {
    font-weight: 700;
    font-size: 0.82rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--color-muted);
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2ecc71;
    box-shadow: 0 0 0 4px rgba(46, 204, 113, 0.15);
  }
`

const PanelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 0;
  border-bottom: 1px dashed var(--color-line);
  font-size: 0.92rem;
  color: var(--color-ink);

  &:last-child {
    border-bottom: none;
  }
  .left {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-width: 0;
    white-space: nowrap;
  }
  @media (max-width: 640px) {
    padding: 0.65rem 0;
    font-size: 0.84rem;
    .left {
      gap: 0.6rem;
    }
    .avatar {
      width: 30px;
      height: 30px;
      font-size: 0.7rem;
      flex-shrink: 0;
    }
  }
  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--color-primary-soft), var(--color-primary));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.78rem;
    font-weight: 700;
  }
  .time {
    font-size: 0.78rem;
    color: var(--color-muted);
    font-weight: 600;
  }
  .status {
    white-space: nowrap;
    padding: 0.25rem 0.7rem;
    font-size: 0.7rem;
    font-weight: 700;
    color: var(--color-primary);
    background: var(--color-bg-soft);
    border: 1px solid var(--color-line);
    border-radius: var(--radius-pill);
  }
`

/* -------- Section -------- */
const Section = styled.section`
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 3.5rem 1.5rem;

  @media (min-width: 1440px) {
    padding: 4rem 2rem;
  }
  @media (max-width: 768px) {
    padding: 2.5rem 1.25rem;
  }
`

const SectionHead = styled.div`
  max-width: 720px;
  margin-bottom: 2.25rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #d5728a;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Playfair Display', 'Noto Serif TC', serif;
    font-size: clamp(1.55rem, 3.5vw, 2.6rem);
    font-weight: 700;
    color: #1b0d12;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  p {
    font-size: 1.02rem;
    line-height: 1.8;
    color: #6b3547;
  }
`

/* -------- Modules -------- */
const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;

  @media (min-width: 1440px) {
    gap: 1.5rem;
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }
`

const ModuleCard = styled(motion.div)`
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-lg);
  padding: 2rem 1.75rem;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-primary);
    box-shadow: var(--shadow-card-hover);
  }

  .icon {
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-soft);
    color: var(--color-primary);
    border-radius: var(--radius-sm);
    margin-bottom: 0.75rem;
  }
  h4 {
    font-size: 1.08rem;
    font-weight: 700;
    color: var(--color-ink);
    margin-bottom: 0.35rem;
  }
  p {
    font-size: 0.9rem;
    color: var(--color-muted);
    line-height: 1.7;
  }

  @media (max-width: 640px) {
    padding: 1.1rem 0.95rem;
    min-height: 0;
    gap: 0.3rem;
    border-radius: var(--radius-md);

    .icon {
      width: 36px;
      height: 36px;
      margin-bottom: 0.4rem;
      svg {
        width: 18px;
        height: 18px;
      }
    }
    h4 {
      font-size: 0.92rem;
      margin-bottom: 0.1rem;
    }
    p {
      font-size: 0.78rem;
      line-height: 1.55;
    }
  }
`

/* -------- Flow -------- */
const FlowOuter = styled.section`
  padding: 2rem 1.5rem 4rem;
  @media (min-width: 1440px) {
    padding: 2rem 2rem 5rem;
  }
  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
  }
`

const FlowWrap = styled.div`
  background: linear-gradient(160deg, #1b0d12 0%, #3d1f2a 100%);
  color: #fff;
  padding: 5rem 3rem;
  border-radius: var(--radius-xl);
  max-width: var(--container-wide);
  margin: 0 auto;
  box-shadow: var(--shadow-heavy);

  @media (max-width: 768px) {
    padding: 2.25rem 1.25rem;
    border-radius: var(--radius-lg);
  }
`

const FlowHead = styled.div`
  max-width: 720px;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    p {
      font-size: 0.92rem;
      line-height: 1.7;
    }
  }

  .tag {
    display: inline-block;
    font-size: 0.72rem;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #f5c6d3;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.45rem, 3vw, 2.4rem);
    font-weight: 700;
    color: #fff;
    margin-bottom: 1rem;
    line-height: 1.25;
  }
  p {
    font-size: 1rem;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.75);
  }
`

const FlowSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
  }
`

const FlowStep = styled.div`
  position: relative;
  padding: 1.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(245, 198, 211, 0.18);
  border-radius: var(--radius-lg);

  .num {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: #f5c6d3;
    font-weight: 700;
    margin-bottom: 0.75rem;
    line-height: 1;
  }
  h5 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 0.5rem;
  }
  p {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
  }

  @media (max-width: 640px) {
    padding: 1.1rem 0.95rem;
    border-radius: var(--radius-md);
    .num {
      font-size: 1.4rem;
      margin-bottom: 0.5rem;
    }
    h5 {
      font-size: 0.92rem;
      margin-bottom: 0.3rem;
    }
    p {
      font-size: 0.78rem;
      line-height: 1.55;
    }
  }
`

/* -------- Compare table -------- */
const CompareWrap = styled.div`
  background: #fff;
  border: 1px solid var(--color-line);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-card);

  @media (max-width: 768px) {
    border-radius: var(--radius-lg);
  }
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;

  th,
  td {
    padding: 1.2rem 1.75rem;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid var(--color-line);
  }
  tr:last-child td {
    border-bottom: none;
  }
  th {
    font-family: 'Playfair Display', serif;
    color: var(--color-ink);
    font-weight: 700;
    background: var(--color-bg-soft);
    font-size: 1rem;
  }
  td.label {
    font-weight: 600;
    color: var(--color-ink);
  }
  td.good {
    color: var(--color-ink);
  }
  td.bad {
    color: var(--color-muted);
  }
  @media (max-width: 640px) {
    min-width: 0;
    table-layout: fixed;

    th,
    td {
      text-wrap: balance;
      padding: 0.75rem 0.6rem;
      font-size: 0.78rem;
      line-height: 1.45;
    }
    th {
      font-size: 0.8rem;
    }
    th:first-child,
    td:first-child {
      padding-left: 0.9rem;
    }
    th:nth-child(1) {
      width: 30% !important;
    }
    th:nth-child(2) {
      width: 23% !important;
    }
    th:nth-child(3) {
      width: 47% !important;
    }
    td.good {
      white-space: nowrap;
      padding-right: 0.5rem;
    }
    td span.check {
      width: 18px;
      height: 18px;
      margin-right: 0.15rem;
      vertical-align: -3px;
      svg {
        width: 11px;
        height: 11px;
      }
    }
  }
  td .check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    background: var(--color-primary);
    color: #fff;
    border-radius: 50%;
  }
`

/* ============================================================
 *  Component
 * ============================================================ */

const Services = () => {
  const navigate = useNavigate()

  const modules = [
    {
      icon: <HiOutlineCalendar size={22} />,
      title: '智慧預約引擎',
      desc: '24/7 線上自助預約，支援多設計師、多服務、多門店自動排程。'
    },
    {
      icon: <HiOutlineUserGroup size={22} />,
      title: '顧客 CRM',
      desc: '完整消費紀錄、偏好標籤、過敏備註，讓每位顧客都被記得。'
    },
    {
      icon: <HiOutlineCurrencyDollar size={22} />,
      title: '線上收款 & 訂金',
      desc: '整合第三方金流，訂金制、套票制、課程卡一次搞定。'
    },
    {
      icon: <HiOutlineChartSquareBar size={22} />,
      title: '營運儀表板',
      desc: '營收、回客率、熱門服務、尖峰時段，決策一目瞭然。'
    },
    {
      icon: <HiOutlineBell size={22} />,
      title: '自動化提醒',
      desc: 'LINE、Email、簡訊三通道提醒，降低放鳥率最高 70%。'
    },
    {
      icon: <HiOutlineStar size={22} />,
      title: '評價與口碑',
      desc: '服務後自動邀請評價，整合社群分享，累積品牌聲量。'
    },
    {
      icon: <HiOutlineDeviceMobile size={22} />,
      title: '品牌預約官網',
      desc: '專屬網址與預約頁面，IG 連結、作品集、服務清單一站整合。'
    },
    {
      icon: <HiOutlineSparkles size={22} />,
      title: '會員行銷',
      desc: '生日優惠、回訪提醒、點數累積，把一次性顧客變粉絲。'
    }
  ]

  return (
    <Page>
      {/* Hero */}
      <Hero>
        <HeroBg>
        <HeroInner>
          <HeroText>
            <span className='tag'>Flagship Product · Beautefeel</span>
            <h1>
              為美業打造的<br />
              <em>一站式雲端營運平台</em>
            </h1>
            <p>
              Beautefeel 整合預約、顧客、金流、行銷、數據五大模組，讓美業店家專注於服務本質，
              其餘的繁瑣雜務，交給一個漂亮、穩定、持續進化的平台。
            </p>
            <Actions>
              <PrimaryBtn
                onClick={() => window.open('https://beautefeel.com', '_blank')}
              >
                前往 Beautefeel 官網 <FiArrowUpRight />
              </PrimaryBtn>
              <GhostBtn onClick={() => navigate('/contact')}>
                預約導入諮詢
              </GhostBtn>
            </Actions>
          </HeroText>

          <HeroPanel
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <PanelHead>
              <span className='title'>今日預約 · Today</span>
              <span className='dot' />
            </PanelHead>
            <PanelRow>
              <div className='left'>
                <div className='avatar'>Li</div>
                <div>
                  <div>林小姐 · 光療美甲</div>
                  <div className='time'>10:30 — 12:00</div>
                </div>
              </div>
              <span className='status'>已付訂金</span>
            </PanelRow>
            <PanelRow>
              <div className='left'>
                <div className='avatar'>Ch</div>
                <div>
                  <div>陳先生 · 頭皮 SPA</div>
                  <div className='time'>13:00 — 14:30</div>
                </div>
              </div>
              <span className='status'>會員</span>
            </PanelRow>
            <PanelRow>
              <div className='left'>
                <div className='avatar'>Wu</div>
                <div>
                  <div>吳小姐 · 嫁接美睫</div>
                  <div className='time'>15:00 — 17:00</div>
                </div>
              </div>
              <span className='status'>新客</span>
            </PanelRow>
            <PanelRow>
              <div className='left'>
                <div className='avatar'>Ya</div>
                <div>
                  <div>楊小姐 · 醫美諮詢</div>
                  <div className='time'>18:00 — 19:00</div>
                </div>
              </div>
              <span className='status'>VIP</span>
            </PanelRow>
          </HeroPanel>
        </HeroInner>
        </HeroBg>
      </Hero>

      {/* Modules */}
      <Section>
        <SectionHead>
          <span className='tag'>Product Modules</span>
          <h2>
            八大核心模組，<br />
            覆蓋美業營運全流程
          </h2>
          <p>
            Beautefeel
            不是單一功能，而是為美業設計的完整營運作業系統。你看到的每一個模組，都來自與上百家店家長期對話後的累積。
          </p>
        </SectionHead>

        <ModuleGrid>
          {modules.map((m, i) => (
            <ModuleCard
              key={m.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <div className='icon'>{m.icon}</div>
              <h4>{m.title}</h4>
              <p>{m.desc}</p>
            </ModuleCard>
          ))}
        </ModuleGrid>
      </Section>

      {/* Flow */}
      <FlowOuter>
      <FlowWrap>
        <div>
          <FlowHead>
            <span className='tag'>Onboarding Flow</span>
            <h2>
              四步驟，<br />
              把你的品牌搬上 Beautefeel
            </h2>
            <p>
              從註冊到上線最快 48
              小時。我們提供一對一顧問陪伴導入，讓你不必獨自面對系統切換的每一步。
            </p>
          </FlowHead>
          <FlowSteps>
            <FlowStep>
              <div className='num'>01</div>
              <h5>免費諮詢</h5>
              <p>了解你的營運痛點，盤點現有流程與未來目標。</p>
            </FlowStep>
            <FlowStep>
              <div className='num'>02</div>
              <h5>專屬建置</h5>
              <p>協助上架服務、設計師資料、品牌頁面與連結串接。</p>
            </FlowStep>
            <FlowStep>
              <div className='num'>03</div>
              <h5>員工培訓</h5>
              <p>團隊線上或現場培訓，15 分鐘就能上手後台操作。</p>
            </FlowStep>
            <FlowStep>
              <div className='num'>04</div>
              <h5>正式上線</h5>
              <p>持續顧問支援、每月優化建議，數據陪你一起成長。</p>
            </FlowStep>
          </FlowSteps>
        </div>
      </FlowWrap>
      </FlowOuter>

      {/* Compare */}
      <Section>
        <SectionHead>
          <span className='tag'>Why Beautefeel</span>
          <h2>為什麼不用 LINE、Google 表單就好？</h2>
          <p>
            很多店家在成長初期會依賴 LINE 與 Excel
            手動管理，但當每月預約量超過百筆時，這些工具反而成為營收瓶頸。
          </p>
        </SectionHead>

        <CompareWrap>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '40%' }}>營運情境</th>
                <th style={{ width: '30%' }}>LINE / 表單</th>
                <th style={{ width: '30%' }}>Beautefeel</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className='label'>深夜預約受理</td>
                <td className='bad'>需人工回覆</td>
                <td className='good'>
                  <span className='check'>
                    <FiCheck size={14} />
                  </span>{' '}
                  自動化處理
                </td>
              </tr>
              <tr>
                <td className='label'>避免時段重複預約</td>
                <td className='bad'>需手動核對</td>
                <td className='good'>
                  <span className='check'>
                    <FiCheck size={14} />
                  </span>{' '}
                  系統自動鎖定
                </td>
              </tr>
              <tr>
                <td className='label'>顧客偏好與病歷紀錄</td>
                <td className='bad'>散落各群組</td>
                <td className='good'>
                  <span className='check'>
                    <FiCheck size={14} />
                  </span>{' '}
                  一人一卡集中管理
                </td>
              </tr>
              <tr>
                <td className='label'>線上收訂金</td>
                <td className='bad'>無法整合</td>
                <td className='good'>
                  <span className='check'>
                    <FiCheck size={14} />
                  </span>{' '}
                  串接多家金流
                </td>
              </tr>
              <tr>
                <td className='label'>回客率與營收分析</td>
                <td className='bad'>無數據可看</td>
                <td className='good'>
                  <span className='check'>
                    <FiCheck size={14} />
                  </span>{' '}
                  即時儀表板
                </td>
              </tr>
              <tr>
                <td className='label'>品牌形象</td>
                <td className='bad'>像個私人帳號</td>
                <td className='good'>
                  <span className='check'>
                    <FiCheck size={14} />
                  </span>{' '}
                  專屬預約官網
                </td>
              </tr>
            </tbody>
          </Table>
        </CompareWrap>
      </Section>

    </Page>
  )
}

export default Services
